// @erp/ui/app-shell — the persistent chrome: sidebar + header + footer around
// routed content (components/drawer's "Sidebar is always visible on large
// screen, can be toggled on small screen" syntax — lg:drawer-open). This is
// the AppShell each portal's rootRoute now mounts (ADR: "app.tsx # shell: nav
// chrome, error/suspense roots"), replacing the bare `<Outlet/>`. Deciding
// WHEN to show chrome (e.g. not on /login) stays the portal's job — this
// component only renders it.
import { For, Show, createEffect, createSignal, createUniqueId, on, onMount, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import { LayoutGrid } from "lucide-solid";
import { BRAND_BAND } from "../brand/index.js";
import { Link } from "@tanstack/solid-router";
import type { NavModule } from "@erp/auth-kit/nav";
import { resolveActiveModule } from "@erp/auth-kit/nav";
import { installFocusModality } from "../focus-modality/index.js";
import { Sidebar, SidebarPanel } from "../sidebar/index.js";
import { Header } from "../header/index.js";
import type { HeaderUser } from "../header/index.js";
import { Footer } from "../footer/index.js";
import { CommandPalette } from "../command-palette/index.js";
import { PageBoundary } from "../error-boundary/index.js";
import { RouteProgress } from "../route-progress/index.js";

/* Hidden until focused: the sr-only clip lifts on :focus and the link pins
   top-left over the header as an accent button — one ring, one colour, the
   same recipe as every button (tokens.css). */
const SKIP_LINK =
  "sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-[110] focus:rounded-300 focus:bg-primary focus:px-3 focus:py-2 focus:text-sm focus:font-medium focus:text-primary-content";

export function AppShell(props: {
  modules: NavModule[];
  pathname: string;
  user: HeaderUser | null;
  onSignOut?: () => void | Promise<void>;
  onProfile?: () => void;
  /** Extra header controls before the display toggles — e.g. a public portal's Sign in button. */
  headerActions?: JSX.Element;
  appName?: string;
  /** Brand-mark colour (fixed asset colour) — e.g. BRAND_PINK for the staff desk. */
  brandColor?: string;
  /** The portal's theme pair for the header toggle. */
  themes?: { light: string; dark: string };
  /** Tenant line under the sidebar logo — hidden when absent. */
  institutionName?: string;
  version?: string;
  children: JSX.Element;
}) {
  const drawerId = createUniqueId();
  // Keyboard-vs-pointer focus tracking (tokens.css gates the text-field ring
  // on it — the Spectrum textfield's is-keyboardFocused, as one attribute).
  // Idempotent, so a portal mounting two shells over its lifetime is fine.
  installFocusModality();

  /* ── Nav scroll + overlay discipline across navigations ──────────────────
   * The chrome persists across route changes, but two things can still make a
   * click feel like a reload: (a) in the <lg overlay mode the drawer stayed
   * open over the new page, and (b) anything that reflows the menu (submenu
   * collapse, zoomed layouts, content swap) can clamp the nav's scrollTop.
   * So the shell OWNS the guarantee: on navigation the overlay closes, and
   * within the same module the nav's scroll position is restored verbatim;
   * a module switch is a new menu and deliberately starts at the top. */
  let sideRef: HTMLDivElement | undefined;
  let toggleRef: HTMLInputElement | undefined;
  let mainRef: HTMLElement | undefined;
  let navRef: HTMLDivElement | undefined;
  const closeDrawer = () => {
    if (toggleRef) toggleRef.checked = false;
  };
  let savedScroll = 0;
  let savedModule = "";
  /* The strip's visibility is a PER-VIEWER convenience, so it lives in
     localStorage rather than the URL or the server: nobody deep-links to a
     hidden band, and a colleague opening a shared link should get the default.
     Every read and write is guarded — a private window or blocked site data
     throws on access, and the shell must still render. */
  const STRIP_KEY = "erp.modules.open";
  const [modulesOpen, setModulesOpen] = createSignal(true);
  onMount(() => {
    try {
      if (localStorage.getItem(STRIP_KEY) === "0") setModulesOpen(false);
    } catch {
      /* no stored preference reachable — the default stands */
    }
  });
  const toggleModules = () => {
    const next = !modulesOpen();
    setModulesOpen(next);
    try {
      localStorage.setItem(STRIP_KEY, next ? "1" : "0");
    } catch {
      /* the band still toggles for this page view */
    }
  };

  const activeModuleId = () => resolveActiveModule(props.pathname, props.modules)?.id ?? "";
  createEffect(
    on(
      () => props.pathname,
      (_pathname, prev) => {
        if (prev === undefined) return; // initial mount — nothing to restore
        if (toggleRef?.checked) toggleRef.checked = false; // overlay closes on nav
        const target = activeModuleId() === savedModule ? savedScroll : 0;
        const apply = () => {
          if (sideRef && sideRef.scrollTop !== target) sideRef.scrollTop = target;
        };
        // after THIS frame's reflow (the new menu/outlet has laid out) — with a
        // microtask fallback for jsdom, which has no rAF loop.
        if (typeof requestAnimationFrame === "function") requestAnimationFrame(apply);
        else queueMicrotask(apply);
      },
    ),
  );

  return (
    /* THE BANDS SPAN THE FULL WIDTH (2026-10-02, the owner: "I need the exact
       same layout"). Adobe stacks [app strip] and [grid + wordmark + menus]
       edge to edge, and only BELOW them does the rail/list sit beside the
       content. Ours had the drawer as the outermost box, which put the sidebar
       (and the wordmark inside it) to the LEFT of the header — so the toggle
       could never sit left of the mark. The drawer now wraps only the row
       under the header, and keeps every bit of its behaviour: `lg:drawer-open`
       still pins the sidebar open on a laptop and it is still an overlay on a
       phone. */
    <div class="flex h-screen flex-col overflow-hidden bg-base-200">
      {/* The source's frame (DashboardLayout): a full-height column beside the
          sidebar — white header flush on top, and the GREY body carrying one
          card-like rounded corner (top-left) where it meets the white chrome —
          rounded-tl-800, the 16px corner-radius-800 step named on the ramp
          (DEV-GUIDE-002 §12.1: stock Tailwind radii never appear by name).
          h-screen + internal scrolling, so the chrome never scrolls away. */}
      <div class="contents">
        {/* SKIP LINKS — the first tab stops on every screen (audit UX-13: the
            sidebar sits AFTER main in the DOM, so a keyboard user tabbed through
            the whole page — 26 stops on Titles — before reaching navigation).
            Visually hidden until focused, then pinned top-left over the header.
            Focus is moved by hand rather than by the hash: a hash write would
            reach the router's history, and on a phone "navigation" must first
            OPEN the drawer. */}
        <a
          href="#main-content"
          class={SKIP_LINK}
          onClick={(e) => {
            e.preventDefault();
            mainRef?.focus();
          }}
        >
          Skip to content
        </a>
        <a
          href="#primary-navigation"
          class={SKIP_LINK}
          onClick={(e) => {
            e.preventDefault();
            if (toggleRef && !toggleRef.checked && typeof matchMedia === "function" && matchMedia("(max-width: 1023px)").matches) {
              toggleRef.checked = true; // <lg: the drawer must be open to be reached
            }
            const target = navRef?.querySelector<HTMLElement>("a[href], button:not([disabled])") ?? navRef;
            const go = () => target?.focus();
            if (typeof requestAnimationFrame === "function") requestAnimationFrame(go);
            else go();
          }}
        >
          Skip to navigation
        </a>
        {/* THE MODULE STRIP — Adobe's band one (2026-10-02, the owner, after
            reading adobe.com/files: "other areas can be above header just like
            Adobe"). Their chrome separates GLOBAL from LOCAL by band: a top
            strip switches product, the left rail and list navigate inside the
            one you are in. Ours had both jobs in the same sidebar — the
            module list sat at its BOTTOM, under every section of the current
            module, which on a phone meant opening the drawer and scrolling
            past everything to leave the module you were in.

            Horizontal `overflow-x-auto` on the strip itself, never the page:
            six modules fit a laptop outright and the strip scrolls its own box
            on a phone, which is the same rule the DataTable's frame follows.
            Active takes the neutral wash the sidebar rows take, so "where am
            I" reads the same in both bands and accent stays with the verbs. */}
        <Show when={props.modules.length > 1 && modulesOpen()}>
          <nav
            id="module-strip"
            aria-label="Modules"
            /* 56px, and the toggle centred over the RAIL's 64px column (ps-3 puts
                its 40px box at x=12). Both of Adobe's bands are 56 and their
                rail items sit at x=4 in a 64px column; measured 2026-10-02. */
            style={{ "background-image": BRAND_BAND }}
            class="flex h-14 shrink-0 items-center gap-1 overflow-x-auto pe-4 ps-3"
          >
            {/* THE TOGGLE RIDES THE TOP BAND (2026-10-02, the owner: "its
                location is wrong"). Adobe's grid glyph sits in the TOP-LEFT
                CORNER of the chrome, over the rail's column — not partway
                along the row below it. When the strip is showing it belongs
                here, at its start; when it is hidden the header carries it, so
                the glyph is always in the same corner. */}
            <button
              type="button"
              id="strip-toggle-modules"
              aria-label="Hide modules"
              aria-expanded={true}
              aria-controls="module-strip"
              class="flex size-10 shrink-0 items-center justify-center rounded-100 text-band-content transition-colors hover:bg-band-hover focus-visible:bg-band-hover [&_svg]:[stroke-width:2.5]"
              onClick={toggleModules}
            >
              <LayoutGrid size={20} aria-hidden="true" />
            </button>
            <For each={props.modules}>
              {(m) => (
                <Link
                  to={m.to}
                  /* HEAVIER STROKE on the band (2026-10-02, the owner: the
                     labels went bold and the icons stayed light beside them).
                     FILLING them was tried first and rejected on sight: lucide
                     draws outlines with no interior shapes, so `fill-current`
                     turned Content into a solid rectangle and Finance into a
                     disc. The weight of an outline glyph lives in its stroke,
                     so the stroke goes to 2.5 — bold beside bold, still legible
                     as itself. Scoped to this band; the kit's icons elsewhere
                     keep the 1.5 outline Spectrum draws. */
                  class={`flex h-8 shrink-0 items-center gap-1.5 rounded-100 px-2.5 text-sm font-bold transition-all [&_svg]:[stroke-width:2.5] ${
                    m.id === activeModuleId()
                      ? "bg-band-selected text-band-content"
                      : "text-band-content hover:bg-band-hover focus-visible:bg-band-hover"
                  }`}
                  aria-current={m.id === activeModuleId() ? "page" : undefined}
                >
                  <Show when={m.icon}>
                    <span class="flex shrink-0">
                      <Dynamic component={m.icon!} size={14} />
                    </span>
                  </Show>
                  <span class="truncate">{m.label}</span>
                </Link>
              )}
            </For>
          </nav>
        </Show>
        <Header
          drawerId={drawerId}
          appName={props.appName}
          brandColor={props.brandColor}
          {...(props.modules.length > 1 && !modulesOpen() ? { modulesOpen: false, onToggleModules: toggleModules } : {})}
          user={props.user}
          actions={props.headerActions}
          themes={props.themes}
        />
        {/* The route progress bar rides the header's bottom edge: a 2px accent
            sweep while a route match or a query is in flight for longer than
            150ms, so a click on the sidebar is visibly TAKEN before the outlet
            fills (audit UX-01: the outlet sat empty for the full API latency
            and people clicked twice). It takes no height — the wrapper is h-0
            and the bar overlays main's top edge — so nothing below shifts. */}
        <RouteProgress />
        {/* Only the ROW below the chrome is the drawer: sidebar beside content,
            pinned open from `lg`, an overlay under it. `min-h-0` is load-bearing
            in a flex column — without it this row takes its content's height and
            main's own scrollport never forms. */}
        <div class="drawer min-h-0 flex-1 lg:drawer-open">
      {/* daisyUI's drawer state lives in this checkbox. It is visually hidden —
          the HEADER's hamburger button (a real <button>, UX-13) and the overlay
          button below drive it — so it leaves the tab order and the tree:
          before this it was a focusable stop with no visible ring and an
          "unlabelled checkbox" announcement. */}
      <input
        ref={toggleRef}
        id={drawerId}
        type="checkbox"
        class="drawer-toggle"
        tabindex="-1"
        aria-hidden="true"
        onChange={(e) => {
          // Overlay mode: a hidden drawer clamps its scrollTop to 0, so restore
          // the saved position when it opens — the menu reopens where it was.
          if (!e.currentTarget.checked) return;
          const apply = () => {
            if (sideRef && activeModuleId() === savedModule) sideRef.scrollTop = savedScroll;
          };
          if (typeof requestAnimationFrame === "function") requestAnimationFrame(apply);
          else queueMicrotask(apply);
        }}
      />
        <div class="drawer-content flex min-h-0 flex-col">
        {/* The page padding sits on the INNER box, not on <main> (2026-09-03).
            <main> is the page's scroll container, and a `sticky top-0` child
            pins to its CONTENT box — so with the padding here, anything that
            pins (a filter bar, a table header) stops 24px down and the content
            it is meant to cover scrolls visibly through the strip above it.
            With the padding below, the scrollport's top edge IS the content's
            top edge and pinned things sit flush under the chrome. Identical at
            rest; the only change is that the 1600px cap now includes the
            padding instead of sitting inside it. */}
        <main
          ref={mainRef}
          id="main-content"
          tabindex="-1"
          class="relative flex flex-1 overflow-y-auto rounded-t-800 bg-base-100 outline-none lg:me-2"
        >
          {/* THE SECTION PANEL LIVES IN HERE (2026-10-02, the owner: "their
              second sidebar is inside the main"). Measured on adobe.com/files:
              `.left-panel` is a child of `fw-body` — the content region — while
              the rail sits outside it. So the panel takes the content's own
              ground, carries no divider, and sticks to the top of the
              scrollport while the page scrolls past it. */}
          <SidebarPanel
            modules={props.modules}
            pathname={props.pathname}
            institutionName={props.institutionName}
            class="sticky top-0 hidden max-h-screen rounded-tl-800 bg-transparent lg:flex"
          />
          <div class="mx-auto w-full max-w-[1600px] min-w-0 p-4 sm:p-6">
            {/* per-page error wall — a crashed screen never blanks the shell;
                navigating away (pathname key) or "Try again" remounts it */}
            <PageBoundary resetKey={props.pathname}>{props.children}</PageBoundary>
          </div>
        </main>
        <Footer appName={props.appName} version={props.version} brandColor={props.brandColor} />
        </div>
      <div
        ref={sideRef}
        /* `lg:overflow-visible` — daisyUI clips `.drawer-side` (hidden/auto),
           which is right while it is an overlay and wrong once it is pinned:
           the rail is ICONS ONLY, so its tooltips ARE its labels, and they are
           painted outside the 56px column. Below `lg` the clip stays, where the
           drawer is a sliding panel that must not leak. */
        class="drawer-side z-20 lg:overflow-visible"
        onScroll={(e) => {
          savedScroll = e.currentTarget.scrollTop;
          savedModule = activeModuleId();
        }}
      >
        {/* The scrim is a BUTTON, not a <label for> (UX-13): aria-label is
            prohibited on a label, and a label is not a keyboard stop, so the
            open drawer had no way to be closed without a pointer. Unchecking
            the toggle directly is enough — the onChange above only runs on
            OPEN. daisyUI's .drawer-overlay recipe (sticky, stretch, the scrim
            colour from spectrum.css) does not care which element wears it.
            lg:hidden — at lg the drawer is pinned open (lg:drawer-open) and
            daisyUI lays .drawer-side out as a BLOCK: an empty inline-block
            button there takes a 24px line box and pushes the sidebar down
            (measured 2026-09-18), and it would be an invisible "Close
            navigation" tab stop on every desktop screen. The empty <label>
            it replaced generated no line box; the button must not either. */}
        <button type="button" aria-label="Close navigation" class="drawer-overlay lg:hidden" onClick={closeDrawer} />
        {/* WIDTH COMES FROM THE SIDEBAR NOW (2026-10-02). It is two bands —
            a 56px icon rail and a 240px panel that appears only for an area
            with sections — so a fixed 224px would either crop the pair or
            leave a gap beside the rail alone. `w-fit` lets the drawer be
            exactly as wide as whichever shape is showing. */}
        <div ref={navRef} id="primary-navigation" tabindex="-1" class="flex h-full w-fit bg-base-100 outline-none lg:bg-transparent">
          <Sidebar
            hideAreaLists={props.modules.length > 1}
            modules={props.modules}
            pathname={props.pathname}
            institutionName={props.institutionName}
            user={props.user}
            onSignOut={props.onSignOut}
            onProfile={props.onProfile}
          />
          {/* BELOW `lg` THE PANEL COMES INTO THE DRAWER. In the content region
              it is `hidden lg:flex`, because 240px beside the page on a phone
              leaves nothing for the page — but without a second mount the
              drawer would be a 56px rail of icons and an area's screens would
              be unreachable on a phone entirely. Exactly one of the two is ever
              displayed, so the other leaves the accessibility tree with it. */}
          <SidebarPanel
            modules={props.modules}
            pathname={props.pathname}
            institutionName={props.institutionName}
            class="lg:hidden"
          />
        </div>
      </div>
      </div>
      </div>
      {/* Mounted ONCE for the whole console: the ⌘K handler and its overlay are
          global, and the header's trigger only flips the shared signal. */}
      <CommandPalette modules={props.modules} />
    </div>
  );
}
